import React, {
  Dispatch,
  ForwardedRef,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react"
import styled from "styled-components"
import { useConfig } from "~/composable/useConfig"
import { SignDocument } from "~/model/Contract"
import AcCheckBox from "~/components/atoms/AcCheckBox"
import AcCells from "~/components/molecules/AcCells"
import { isMobile } from "~/utils/functions"

export type DocumentSignInputValues = {
  [KEY in string]: string | number
}

interface Props {
  signDocument: SignDocument
  isContractor?: boolean
  isConfirm?: boolean
  values: DocumentSignInputValues
  onChange?: (values: DocumentSignInputValues) => void
  isMeeting?: boolean
  customClassname?: string
}

export type SignColumnName = Record<string, { title: string; required: boolean }>

export type DocumentSignInputRef = {
  getSignColumnNames: () => SignColumnName
} & React.ReactNode

export default React.forwardRef(
  (props: PropsWithChildren<Props>, ref: ForwardedRef<DocumentSignInputRef>) => {
    const {
      signDocument,
      isContractor = false,
      isConfirm = false,
      values,
      isMeeting = false,
      customClassname,
    } = props
    const config = useConfig()
    const checkType = config.getConfigKeyData("checkType")
    const documentType = config.getConfigKeyData("documentType")
    const signColumns = config.getConfigKeyData("workflowItem")
    
    const [checkData, setCheckData] = useState([])
    const [inputValues, setInputValues] = useState(values)
    const [dataSource, setDataSource] = useState<any[]>()

    useEffect(() => {
      const checkData = documentType[signDocument?.workflowType]?.values.checks.map((type) => {
        return {
          title: checkType[type].label,
          value: checkType[type].value,
          dataSource: {
            label: checkType[type].values.description,
            value: true,
          },
        }
      })
      setCheckData(checkData)
      const updatedColumns = signDocument?.columns?.map((id) => {
        const columnValues = signColumns[id]?.values

        if (!isMeeting) {
          return columnValues
        }

        return columnValues.map((item) => {
          // Update contents array
          if (Array.isArray(item.contents)) {
            const newContents = item.contents.map((contentItem) => {
              if (contentItem.objectId === "address1" && contentItem.data?.validations) {
                // set validations.required = false
                return {
                  ...contentItem,
                  data: {
                    ...contentItem.data,
                    validations: {
                      ...contentItem.data.validations,
                      required: false,
                    },
                  },
                }
              }
              return contentItem
            })

            item = { ...item, contents: newContents }
          }
          return item
        })
      })
      setDataSource(updatedColumns)
    }, [])

    const getSignColumnNames = () => {
      const flatDataSource = dataSource.flat()

      const transformedDataSource = flatDataSource.map((column) =>
        column.contents.map((content) => [
          content.objectId,
          { title: column.title, required: content?.data?.validations?.required },
        ])
      )

      const result = Object.fromEntries(transformedDataSource.flat())

      return result
    }

    useEffect(() => {
      setInputValues(values)
    }, [values])

    useImperativeHandle(ref, () => ({
      getSignColumnNames,
    }))

    return (
      <>
        {!isConfirm && (
          <>
            <CheckList>
              {checkData?.map((check, index) => (
                <React.Fragment key={index}>
                  <H4>
                    {index + 1}、{check.title}
                  </H4>
                  <Inputs>
                    <AcCheckBox
                      name={`${check.value}`}
                      disabled={!isContractor}
                      dataSource={check.dataSource}
                      value={inputValues?.[check.value] || false}
                      validations={{ required: true }}
                      error={null}
                      onChange={(value: boolean) => {
                        setInputValues({ ...inputValues, [check.value]: value })
                        props?.onChange?.call(this, { ...inputValues, [check.value]: value })
                      }}
                    />
                  </Inputs>
                </React.Fragment>
              ))}
            </CheckList>
            <H4>
              {checkData.length + 1}、{signDocument?.workflowName}の項目
            </H4>
          </>
        )}

        <Items isConfirm={isConfirm} isMobile={isMobile}>
          {dataSource && (
              <AcCells
                name={""}
                cellData={dataSource}
                values={inputValues}
                disabled={!isContractor}
                onChange={(values) => {
                  setInputValues({ ...inputValues, ...values })
                  props?.onChange?.call(this, { ...inputValues, ...values })
                }}
                isConfirm={isConfirm}
                customClassname={customClassname}
              />
          )}
        </Items>
      </>
    )
  }
)

const CheckList = styled.div``

const Items = styled.div`
  padding: 30px 20px 0 !important;
  background-color: #f5f8fc;
  display: flex;
  flex-direction: column;

  h4 {
    padding-left: 0;
  }

  h4::before {
    display: none;
  }

  button {
    width: 104px;
    height: 33px;
    color: #4f4ce6 !important;
    border: 1px solid #4f4ce6;
    background-color: #ffffff;
    border-radius: 10px;
    padding: 0px;
    align-self: end;
  }

  @media screen and (max-width: 480px) {
    padding: 15px 10px 0 !important;
  }

  ${(props) =>
    !props.isConfirm &&
    `
  .cells > .row {
    display: flex;
    flex-direction: column;
    align-content: flex-start;
    > div {
      width: 100%;
    }
    > div:first-child {
      margin-bottom: 7px;
    }
    .address1 {
      margin-bottom: -30px;
    }
  }`}

  ${(props) =>
    props.isConfirm &&
    `
    background-color: #fff;
    display: flex;
    flex-direction: column;
    padding: 0 10% !important;
    margin-bottom: 20px;
    .label.col-md-1:after {
      content: "：";
      margin: 0 30px;
      text-align: center;
    }
    .label.col-1:after {
      content: "：";
      margin: 0 30px;
      text-align: center;
    }
    .align-items-center.row {
      text-align: right;
    }
    .align-items-center.item-row.row {
      margin-left: 30px;
      text-align: left;
    }
    @media screen and (max-width: 460px) {
    
      padding: 0!important;
      .label.col-md-1:after {
        margin: 0 10px;
      }
      .label.col-1:after {
        margin: 0 10px;
      }
      .align-items-center.item-row.row {
        margin-left: 0px;
        text-align: left;
      }
    }

    .address2-row .label::after { content: ""; }
  `}

  ${(props) =>
    !props.isMobile &&
    props.isConfirm &&
    `
     .address2-row {
      position: relative;
      .align-items-center.row {
        // right: -122px;
        // position: absolute;
        width: 100%;
        top: -25px;
       }
     }`}

  ${(props) =>
    props.isMobile &&
    props.isConfirm &&
    `
     .address2-row {
        position: relative;
        .align-items-center.row {
          right: -56px;
          position: absolute;
          width: 100%;
          top: -17px;
       }
     }`}
`

const H4 = styled.h4`
  padding: 10px 0;

  &::before {
    display: none;
  }
`

const Inputs = styled.div`
  ${(props) => !props.isConfirm && "background-color: #f5f8fc;"}

  padding: 25px 20px !important;
  margin-bottom: 20px;

  div > label {
    font-size: 16px;
  }

  @media screen and (max-width: 480px) {
    padding: 15px !important;
    margin-bottom: 10px;
    div > label {
      font-size: 13px;
    }
  }
`

